import { useContext, useEffect, useRef, useState } from 'react';
import { motion, cubicBezier, useInView, useReducedMotion } from 'motion/react';
import { useDimensions } from '../../context/ThemeContextManagement';
import { calculateDelay, REVEAL_MARGIN } from '../../utils/animations';
import { ComponentInViewContext } from '../component-wrapper/ComponentWrapper';

/**
 * Splits its content into words (or characters) and reveals them upwards
 * one unit at a time, masked, once the surrounding ComponentWrapper is in
 * view.
 *
 * GSAP SplitText is used for measuring the word/char breaks only (loaded on
 * demand, split → capture → revert) — all animation is Motion.
 *
 * Pass raw WP markup via `html`, or plain text as `children`. Set
 * `splitBy="chars"` to reveal character by character instead of word by
 * word.
 * `onSplit` receives `{ words, label }` on every (re-)split, e.g. to read
 * `split.words.length`.
 *
 * In-view detection defers to the surrounding ComponentWrapper (so siblings
 * reveal in sync). Used standalone with no wrapper ancestor, it falls back
 * to observing itself. Pass `inView` to override both.
 */
export function AnimateSplitText({
  children,
  html,
  index = 1,
  animationOrder = 0,
  delay = 0,
  duration = 1.2,
  stagger = 0.02,
  splitBy = 'words',
  wordsClass = splitBy === 'chars'
    ? 'AnimateSplitText-char'
    : 'AnimateSplitText-word',
  className = '',
  animated = true,
  onSplit,
  inView,
}) {
  const sourceRef = useRef(null);
  const hasPlayed = useRef(false);
  const [split, setSplit] = useState(null); // { words: [html], label }

  const contextInView = useContext(ComponentInViewContext);
  const selfInView = useInView(sourceRef, {
    once: true,
    margin: REVEAL_MARGIN,
  });
  const isInView = inView ?? contextInView ?? selfInView;
  const reduceMotion = useReducedMotion();
  const { screenWidth } = useDimensions();

  const onSplitRef = useRef(onSplit);
  onSplitRef.current = onSplit;

  // Measure word/char breaks; re-measures when the viewport width changes.
  useEffect(() => {
    if (!sourceRef.current) return;

    let cancelled = false;

    (async () => {
      const [{ SplitText }] = await Promise.all([
        import('gsap/SplitText'),
        document.fonts.ready, // measure with the real font, not the fallback
      ]);
      if (cancelled || !sourceRef.current) return;

      const units = [];
      const wordEnds = [];

      if (splitBy === 'chars') {
        // Char splits only wrap non-whitespace characters, so the space
        // between words is a plain text node GSAP leaves out of `.chars` —
        // reconstruct it from the word boundaries instead.
        const instance = new SplitText(sourceRef.current, {
          type: 'words, chars',
        });
        instance.words.forEach((wordEl) => {
          const chars = Array.from(wordEl.children);
          chars.forEach((charEl, ci) => {
            units.push(charEl.innerHTML);
            wordEnds.push(ci === chars.length - 1);
          });
        });
        const label = sourceRef.current.textContent;
        instance.revert();

        const result = { words: units, wordEnds, label };
        onSplitRef.current?.(result);
        setSplit(result);
        return;
      }

      const instance = new SplitText(sourceRef.current, { type: 'words' });
      instance.words.forEach((word) => units.push(word.innerHTML));
      const label = sourceRef.current.textContent;
      instance.revert();

      const result = { words: units, label };
      onSplitRef.current?.(result);
      setSplit(result);
    })();

    return () => {
      cancelled = true;
    };
  }, [screenWidth, html, children, splitBy]);

  // Once the reveal has *finished*, later re-splits (resize) must not replay
  // it — freshly mounted words render directly in their final state. Set from
  // the last unit's onAnimationComplete, not from `isInView`: flipping it the
  // moment the trigger fires marks the reveal as played while it is still
  // mid-flight, so a re-split during the animation snaps everything to its end
  // state instead of letting it run.
  const markPlayed = () => {
    if (isInView) hasPlayed.current = true;
  };

  if (!children && !html) return null;

  const contentProps = html
    ? { dangerouslySetInnerHTML: { __html: html } }
    : { children };

  if (!animated) return <span className={className} {...contentProps} />;

  const easing = cubicBezier(0.43, 0.195, 0.02, 1);

  const maskVariants = {
    hidden: { clipPath: 'inset(0% 0% 120% 0%)' },
    visible: (i) => ({
      clipPath: 'inset(0% 0% -10% 0%)',
      transition: {
        delay: calculateDelay(animationOrder, index, delay) + i * stagger,
        duration,
        ease: easing,
      },
    }),
  };

  const wordVariants = {
    hidden: { y: '80%', opacity: 0 },
    visible: (i) => ({
      y: '0%',
      opacity: 1,
      transition: {
        delay: calculateDelay(animationOrder, index, delay) + i * stagger,
        duration,
        ease: easing,
        opacity: { duration: duration + 0.5 },
      },
    }),
  };

  return (
    <span
      className={className}
      style={{ position: 'relative' }}
      role={split ? 'text' : undefined}
      aria-label={split ? split.label : undefined}
    >
      {/* Source content: kept visually hidden (but in flow, so SplitText can
          measure it) until split, then kept in place out of flow so
          re-splits on resize can re-measure it. */}
      <span
        ref={sourceRef}
        aria-hidden={split ? true : undefined}
        style={
          split
            ? {
                position: 'absolute',
                inset: 0,
                visibility: 'hidden',
                pointerEvents: 'none',
              }
            : { visibility: 'hidden' }
        }
        {...contentProps}
      />

      {split &&
        split.words.map((word, i) => {
          // GSAP strips the space between units \u2014 add it back after every
          // word (word splits) or after the last char of each word (char
          // splits, using the word boundaries captured at measure time).
          const needsSpace = splitBy === 'chars' ? split.wordEnds[i] : true;
          const html = needsSpace ? word + '\u00A0' : word;

          return reduceMotion ? (
            <span
              key={`${i}-${word}`}
              aria-hidden="true"
              className={wordsClass}
              style={{ display: 'inline-block', verticalAlign: 'top' }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <motion.span
              key={`${i}-${word}`}
              aria-hidden="true"
              className={wordsClass}
              variants={maskVariants}
              custom={i}
              initial={hasPlayed.current ? false : 'hidden'}
              animate={isInView ? 'visible' : 'hidden'}
              onAnimationComplete={
                i === split.words.length - 1 ? markPlayed : undefined
              }
              style={{
                display: 'inline-block',
                verticalAlign: 'top',
                willChange: 'clip-path',
              }}
            >
              <motion.span
                variants={wordVariants}
                custom={i}
                initial={hasPlayed.current ? false : 'hidden'}
                animate={isInView ? 'visible' : 'hidden'}
                style={{ display: 'inline-block', willChange: 'transform' }}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </motion.span>
          );
        })}
    </span>
  );
}
